import catalog from './templateBuilder.json'
import {
  BANNER_FILL_CARD, BANNER_FILL_HEADER, BANNER_FILL_VARIABLE, BANNER_FILLS, BANNER_FOOTER_TRANSPARENT, BANNER_HEAD, BANNER_IMAGE_VARIABLE, BANNER_POSITION_VARIABLE, BANNER_SIZE_VARIABLE, CARD_PICTURE_FOOTER,
  IMAGE_BAND_CSS, IMAGE_BANNER_CENTER_CSS, IMAGE_BANNER_CSS, IMAGE_CARD_CSS, IMAGE_CARD_FULL_CSS, IMAGE_CARD_MARK_CSS, LAYOUT_MARK_CSS, MARK_CSS, MAX_BANNER_SCALE, MIN_BANNER_SCALE,
  TITLE_ALIGNMENT_CENTER, TITLE_CENTER_BADGE_CSS, TITLE_CENTER_CSS, TITLE_COUNTER_CSS, TITLE_NONE_CARD_BADGE_CSS, TITLE_NONE_HEADLESS_CSS, TITLE_PLACEMENT_NONE, TITLE_PLACEMENT_OVER,
  bannerFillOf, bannerFooterOf, bannerImageSize, bannerScaleOf, bannerTitleOf, defaultBannerPosition, titleNoneCss,
} from './bannerFills.js'
import { MIN_TEXT_CONTRAST, contrastRatio, isHexColor, luminance, mix, readableOn } from './colorMath.js'

const IMAGE_POSITION_PATTERN = /^(\d{1,3})% (\d{1,3})%$/
const IMAGE_SIZE_PATTERN = /^(?:(\d{3})% auto|auto (\d{3})%)$/
const CSS_IMAGE_URL_PATTERN = /^url\("(.+)"\)$/
const WHITESPACE_RUN_PATTERN = /\s+/g

// The fills, the title placements (#946) and their CSS live in bannerFills.js, which the widget imports on its own.
export {
  BANNER_FILL_CARD, BANNER_FILL_HEADER, BANNER_FILL_VARIABLE, BANNER_FILLS, BANNER_FOOTER_TINTED, BANNER_FOOTER_TRANSPARENT, BANNER_FOOTERS, BANNER_IMAGE_VARIABLE, BANNER_POSITION_VARIABLE, BANNER_SIZE_VARIABLE,
  CARD_PICTURE_BACKGROUND, CARD_SCRIM_FLOOR, CARD_SECONDARY_TEXT_SHARE, DEFAULT_BANNER_FILL, DEFAULT_BANNER_FOOTER, DEFAULT_BANNER_POSITION, DEFAULT_BANNER_TITLE, IMAGE_BAND_CSS, MAX_BANNER_SCALE, MIN_BANNER_SCALE,
  TITLE_ALIGNMENT_CENTER, TITLE_ALIGNMENT_LEFT, TITLE_ALIGNMENTS, TITLE_PLACEMENT_BELOW, TITLE_PLACEMENT_NONE, TITLE_PLACEMENT_OVER, TITLE_PLACEMENTS,
  bannerFillOf, bannerFooterOf, bannerImageSize, bannerOverlay, bannerScaleOf, bannerTitleOf, cardScrim, centerWash, defaultBannerPosition, templateWithBannerFill,
} from './bannerFills.js'
// The color arithmetic lives in colorMath.js, which the widget imports on its own; these two keep their old importers.
export { contrastRatio, isHexColor } from './colorMath.js'

// The template builder: an operator picks a layout, switches features on and
// off and gives two colors; compileBuilderTemplate turns those choices into
// an ordinary card template (HTML, CSS, variables per mode, native styling).
// The widget and the feed API only ever see the compiled template. The
// choices are saved beside it as `builder` so the builder can reopen it.
// Two ways to write code by hand: `css` (#998) is appended after the CSS the
// choices generate and locks none of them; `code` replaces the HTML and CSS
// outright and locks the layout, banner, font and features.

export const BUILDER_LAYOUTS = catalog.layouts
export const BUILDER_BANNERS = catalog.banners
export const BUILDER_FONTS = catalog.fonts
export const BUILDER_FEATURES = catalog.features
export const BUILDER_DEFAULTS = catalog.defaults

/** Whether a feature's setting is retired in the catalog: not offered, its stored value ignored (TemplateBuilderOptionGroup.Retired). */
export const builderOptionRetired = (feature, option) => BUILDER_FEATURES.find((item) => item.key === feature)?.options?.some((group) => group.key === option && group.retired === true) === true
/** Whether the jerseys' numbers setting is retired in the catalog (false since #1072: the operator supplies the number). */
export const JERSEY_NUMBERS_RETIRED = builderOptionRetired('jerseys', 'numbers')

/**
 * The design answer's options with every retired setting taken from the current choices instead: the model is not
 * told about a retired setting, so its answer cannot carry one, and the saved value must survive a redesign.
 */
export function keepRetiredOptions(current, answer) {
  const merged = {}
  for (const feature of BUILDER_FEATURES) {
    if (!feature.options) continue
    merged[feature.key] = { ...(answer?.[feature.key] || {}) }
    for (const group of feature.options) {
      const kept = current?.[feature.key]?.[group.key]
      if (group.retired && kept !== undefined) merged[feature.key][group.key] = kept
    }
  }
  return merged
}
export const BUILDER_MODES = ['light', 'dark']

// The image banner: its picture is a template variable, so a pack can carry
// its own (the banner worker writes a generated one into template_vars).
// template_builder.go holds the same names and rules for the server side.
export const BANNER_IMAGE = 'image'
// The split banner keeps the header's right 38% for the counter (BANNER_CSS.split), so a hero title never reaches it (titleUnderCounter).
const BANNER_SPLIT = 'split'
export const BANNER_IMAGE_SOURCES = ['library', 'ai']
export const MAX_BANNER_PROMPT_RUNES = 240
// The template_vars key of a pack's own scene prompt (never a CSS variable, so it stays inside Studio).
export const BANNER_PROMPT_VAR = 'banner_prompt'
// A pack's picture mode (#606): "dynamic", the worker paints it while it has no picture; "static", never.
// Absent on packs made before #606, which keep the old rule. Never a CSS variable either.
export const BANNER_MODE_VAR = 'banner_mode'
export const BANNER_MODE_DYNAMIC = 'dynamic'
export const BANNER_MODE_STATIC = 'static'
// true when the operator removed the pack's picture: the card shows its colors. Renderers turn it into
// `--banner-image: none`; storage never holds `none`, which older validation rejects.
export const BANNER_HIDDEN_VAR = 'banner_hidden'
// Framing (bannerFills.js has the defaults): the point of the picture kept in view, as [x, y] percentages, or null.
export function bannerPositionOf(position) {
  const match = typeof position === 'string' ? IMAGE_POSITION_PATTERN.exec(position) : null
  return match && Number(match[1]) <= 100 && Number(match[2]) <= 100 ? [Number(match[1]), Number(match[2])] : null
}
/** The zoom inside a stored size value ("250% auto" for the header, "auto 250%" for the card), or null. */
export function bannerScaleFromSize(value) {
  const match = typeof value === 'string' ? IMAGE_SIZE_PATTERN.exec(value) : null
  const scale = match ? Number(match[1] ?? match[2]) / 100 : NaN
  return scale >= MIN_BANNER_SCALE && scale <= MAX_BANNER_SCALE ? scale : null
}
/** Whether `scale` is a zoom the framing accepts (a number from MIN_BANNER_SCALE to MAX_BANNER_SCALE). */
export function isBannerScale(scale) {
  return typeof scale === 'number' && scale >= MIN_BANNER_SCALE && scale <= MAX_BANNER_SCALE
}
// An https URL (or a local development host over http) that can sit inside url("...") in a stylesheet: no quotes, parentheses, backslashes, spaces or CSS punctuation.
const BANNER_IMAGE_URL = /^(?:https:\/\/|http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/)[A-Za-z0-9._~:/?#[\]@!$&*+,=%-]+$/
export function isBannerImageUrl(value) {
  return typeof value === 'string' && value.length <= 2048 && BANNER_IMAGE_URL.test(value)
}
/** The CSS value a picture URL takes in the banner variable. */
export function bannerImageValue(url) {
  return `url("${url}")`
}
/** The picture URL inside a banner variable value, or null. */
export function bannerImageUrl(value) {
  const match = typeof value === 'string' ? CSS_IMAGE_URL_PATTERN.exec(value) : null
  return match && isBannerImageUrl(match[1]) ? match[1] : null
}
const MIN_ACCENT_CONTRAST = 3

const worstContrast = (color, backgrounds) => Math.min(...backgrounds.map((background) => contrastRatio(color, background)))

/** Move a color towards white or black until it reaches the ratio on every background, keeping what it can of its tint. */
function ensureContrast(color, backgrounds, ratio) {
  if (worstContrast(color, backgrounds) >= ratio) return color
  const target = worstContrast('#ffffff', backgrounds) > worstContrast('#000000', backgrounds) ? '#ffffff' : '#000000'
  for (let step = 1; step < 20; step += 1) {
    const candidate = mix(color, target, step / 20)
    if (worstContrast(candidate, backgrounds) >= ratio) return candidate
  }
  return target
}

const VARIABLES = [
  ['--bg', 'Card background'],
  ['--row', 'Row and band tint'],
  ['--text', 'Text'],
  ['--text-dim', 'Secondary text'],
  ['--header', 'Header band'],
  ['--header-end', 'Banner fade'],
  ['--header-text', 'Header text'],
  ['--accent', 'Accent and button'],
  ['--accent-text', 'Text on accent'],
  ['--divider', 'Lines'],
]

/**
 * Place a two-color palette (main, accent) on every variable of one mode.
 * Text always reaches 4.5:1 on the card and row backgrounds, and the accent
 * stays visible on a dark card. The same palette gives the same answer.
 */
export function placePalette(palette, mode) {
  const main = isHexColor(palette?.[0]) ? palette[0].trim().toLowerCase() : BUILDER_DEFAULTS.palette[0]
  const accentInput = isHexColor(palette?.[1]) ? palette[1].trim().toLowerCase() : main
  const dark = mode === 'dark'
  let bg = dark ? mix(main, '#000000', 0.82) : mix(main, '#ffffff', 0.97)
  // A very bright main color still has to give a dark card in dark mode.
  if (dark && luminance(bg) > 0.03) bg = mix(bg, '#000000', 0.6)
  const row = dark ? mix(bg, '#ffffff', 0.08) : mix(main, '#ffffff', 0.9)
  const surfaces = [bg, row]
  const text = ensureContrast(dark ? '#f5f7fa' : mix(main, '#000000', 0.72), surfaces, 7)
  const textDim = ensureContrast(mix(text, bg, 0.38), surfaces, MIN_TEXT_CONTRAST)
  // Dark cards need a visible accent (3:1); on light cards brand yellows stay, but never white on white.
  const accent = ensureContrast(accentInput, [bg], dark ? MIN_ACCENT_CONTRAST : 1.5)
  const headerText = readableOn(main)
  // The banner fades away from its text color, so the fade can only add contrast.
  const headerEnd = mix(main, headerText === '#ffffff' ? '#000000' : '#ffffff', 0.45)
  return {
    '--bg': bg,
    '--row': row,
    '--text': text,
    '--text-dim': textDim,
    '--header': main,
    '--header-end': headerEnd,
    '--header-text': headerText,
    '--accent': accent,
    '--accent-text': readableOn(accent),
    '--divider': mix(text, bg, 0.86),
  }
}

const BASE_CSS = [
  'html,body{width:100%;min-height:100%;background:var(--bg);color:var(--text)}',
  '*,*::before,*::after{box-sizing:border-box}',
  'body{margin:0;padding:0;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}',
  '.card{display:flex;flex-direction:column;width:380px;max-width:100%;overflow:hidden;background:var(--bg)}',
  '.leg:has(strong:empty){display:none}',
  '.head{display:flex;align-items:center;gap:8px;padding:14px 16px 10px}',
  '.tag{flex-shrink:0;max-width:60%;overflow:hidden;padding:3px 7px;border-radius:5px;background:var(--accent);color:var(--accent-text);font-size:10px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;white-space:nowrap;text-overflow:ellipsis}',
  // Long titles and bets stop at two lines so a card keeps a bounded height.
  '.title{display:-webkit-box;flex:1;min-width:0;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:16px;font-weight:750;line-height:1.25;overflow-wrap:anywhere}',
  '.subtitle{padding:0 16px 8px;font-size:12px;line-height:1.3;color:var(--text-dim);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.subtitle:empty{display:none}',
  '.summary{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2;margin:8px 16px 6px;font-size:12px;line-height:1.35;color:var(--text-dim)}.summary:empty{display:none}',
  '.legs{gap:0;padding:0 16px 4px}',
  '.leg{gap:10px;padding:10px 0}',
  '.leg-icon{display:grid;flex-shrink:0;place-items:center;width:32px;height:32px;border-radius:50%;background:var(--row);font-size:15px;line-height:1}',
  '.leg-copy{flex:1}',
  '.leg-copy strong{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:13px;font-weight:700;line-height:1.3;overflow-wrap:anywhere}',
  '.leg-copy small{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:12px;line-height:1.3;color:var(--text-dim);overflow-wrap:anywhere}',
  '.leg-side{display:flex;flex-direction:column;flex-shrink:0;align-items:flex-end;gap:2px;max-width:42%;text-align:right}',
  '.leg-event{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:10px;font-weight:600;color:var(--text-dim);overflow-wrap:anywhere}',
  '.leg-odds{font-size:12px;font-weight:750;font-variant-numeric:tabular-nums}',
  '.placed{display:flex;align-items:center;justify-content:center;gap:5px;padding:6px 16px 12px;font-size:12px;color:var(--text-dim)}.placed svg{width:13px;height:13px;flex-shrink:0}',
  // The bets-placed counter can also sit in the header or float as a badge over it.
  '.head .placed{flex-shrink:0;max-width:46%;margin-left:auto;padding:0;font-size:11px;color:inherit;opacity:.8}.head .placed span{overflow:hidden;white-space:nowrap;text-overflow:ellipsis}.card{position:relative}.placed-badge{position:absolute;top:10px;right:12px;max-width:46%;padding:3px 9px 3px 7px;border-radius:999px;background:var(--accent);font-size:11px;font-weight:700;color:var(--accent-text)}.card:has(>.placed-badge) .head{padding-right:184px}',
  // Only the hero layouts show the leg count and the game in the header.
  '.tag-count,.event{display:none}',
].join('')

// Hero layouts stack the header: a pill with bet type and leg count, a large title, then the game. The header
// counter floats in the pill's row, so the pill stays clear of it; the badge needs no such cap, since BASE_CSS
// already pads the whole header clear of it (capping the pill again left it about 20px wide, #950).
const HERO_HEAD = '.card .head{flex-direction:column;align-items:flex-start;gap:8px;padding:20px 16px 18px}.card .head .placed{position:absolute;top:22px;right:16px;margin:0}.card:has(.head .placed) .tag{max-width:calc(100% - 184px)}.card .event{max-width:100%;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}.card .title{flex:none;font-size:24px;line-height:1.1;-webkit-line-clamp:3}.card .tag{padding:4px 10px;border-radius:999px}.tag-count{display:inline}.event{display:block;font-size:12px;font-weight:600;opacity:.8}.event:empty{display:none}.legs{padding-top:6px}.leg+.leg{border-top:1px solid var(--divider)}'
// Oswald and Lilita One ship with the widget; /prepacks/ URLs are resolved to the bundled files at render time.
export const BUILDER_FONT_FACES = {
  oswald: '@font-face{font-family:"Oswald";font-weight:700;font-display:swap;src:url(/prepacks/oswald-700.woff2) format("woff2")}',
  lilita: '@font-face{font-family:"Lilita One";font-weight:400;font-display:swap;src:url(/prepacks/lilita-one-400.woff2) format("woff2")}',
}
export const BUILDER_FONT_FAMILIES = {
  oswald: '"Oswald",Impact,"Arial Narrow",sans-serif',
  lilita: '"Lilita One","Arial Rounded MT Bold",sans-serif',
}
const OSWALD_TITLE = `font-family:${BUILDER_FONT_FAMILIES.oswald};font-weight:700;letter-spacing:.01em;text-transform:uppercase`

const LAYOUT_CSS = {
  'clean-inline': '.head{border-bottom:1px solid var(--divider)}.subtitle{padding-top:10px}.leg+.leg{border-top:1px solid var(--divider)}',
  'soft-rows': '.legs{gap:8px;padding:2px 12px 8px}.leg{padding:10px 12px;border-radius:10px;background:var(--row)}.leg-icon{background:var(--bg)}',
  'accent-border': '.head{background:var(--row)}.subtitle{padding-top:10px}.legs{padding-top:2px}.leg+.leg{border-top:1px solid var(--divider)}div.placed{margin-top:4px;padding-top:10px;background:var(--row)}',
  // On the header band the tag inverts the band's own colors: the accent may be too close to the main color to show there.
  'bold-header': '.head{padding:14px 16px;background:var(--header);color:var(--header-text)}.tag{background:var(--header-text);color:var(--header)}.subtitle{padding-top:10px}.legs{padding-top:4px}.leg+.leg{border-top:1px solid var(--divider)}',
  minimal: '.tag{padding:0;background:none;color:var(--text-dim)}.head{padding-bottom:4px}.leg{padding:7px 0}.leg-icon{background:none;width:24px}',
  hero: `${HERO_HEAD}.head{background:var(--row)}`,
  spotlight: `${HERO_HEAD}.head{background:radial-gradient(130% 150% at 88% 0%,color-mix(in srgb,var(--accent) 50%,var(--header)) 0%,var(--header) 52%,var(--header-end) 100%);color:var(--header-text)}.tag{background:var(--header-text);color:var(--header)}`,
  editorial: `${BUILDER_FONT_FACES.oswald}${HERO_HEAD}.head{background:linear-gradient(90deg,var(--row) 42%,transparent),repeating-linear-gradient(-55deg,transparent 0 12px,color-mix(in srgb,var(--accent) 34%,transparent) 12px 15px),var(--row)}.card .title{${OSWALD_TITLE};font-size:32px;line-height:1}.tag{border:1.5px solid currentColor;background:transparent;color:inherit}`,
  compact: '.head{padding:10px 14px 6px}.title{font-size:14px}.subtitle{padding:0 14px 6px}.legs{padding:0 14px 2px}.leg{gap:8px;padding:6px 0}.leg+.leg{border-top:1px solid var(--divider)}.leg-icon{width:24px;height:24px;font-size:12px}.leg-copy{flex-direction:row;flex-wrap:wrap;align-items:baseline;column-gap:6px}.leg-copy strong{font-size:12px}.leg-copy small{font-size:11px}div.placed{padding:4px 14px 10px;font-size:11px}',
}

// A banner replaces whatever header the layout draws (BANNER_HEAD).
const BANNER_CSS = {
  none: '',
  gradient: `${BANNER_HEAD}.head{background:linear-gradient(135deg,var(--header),var(--header-end))}`,
  split: `${BANNER_HEAD}.head,.card .head{padding-right:38%}.head{background:linear-gradient(115deg,var(--header) 0 66%,var(--accent) 66% 100%)}.head .placed{color:var(--accent-text);opacity:1}.placed-badge{background:var(--header);color:var(--header-text)}`,
}
const fillsCard = (builder) => builder.banner === BANNER_IMAGE && bannerFillOf(builder.image) === BANNER_FILL_CARD
// The picture runs under the native footer too (#1038): the artwork is see-through and the card shell draws it.
const fillsFullCard = (builder) => fillsCard(builder) && bannerFooterOf(builder.image) === BANNER_FOOTER_TRANSPARENT
// The layouts whose header shows the game: the hero ones, by their header CSS.
const layoutShowsEvent = (layout) => LAYOUT_CSS[layout].includes(HERO_HEAD)

/**
 * Whether a header picture on `layout` sits behind a tall header (BannerHeaderTall in template_builder.go, #979): a
 * hero layout's column header with the title over the picture, which grows with a two-line title to 140px, 185px on
 * Editorial, at the 380px design width. Below the picture or without a title it is the 76px band on every layout.
 */
export function bannerHeaderTall(layout, title, fill = BANNER_FILL_HEADER) {
  return Boolean(LAYOUT_CSS[layout]?.includes(HERO_HEAD)) && bannerTitleOf(title, fill).placement === TITLE_PLACEMENT_OVER
}

/**
 * The shape (width over height) a generated picture is painted in (prepackBannerImageSize in
 * prepack_banner_service.go, #979): square behind the whole card, 2:1 behind a tall header (bannerHeaderTall), else
 * 3:1, the widest the image model paints.
 */
export function bannerPictureAspect(fill, layout, title) {
  if (fill === BANNER_FILL_CARD) return 1
  return bannerHeaderTall(layout, title, fill) ? 2 : 3
}
// Where the bets-placed counter is, when it is on: below the legs, in the header, or a badge over the card's top.
const counterAt = (builder, position) => builder.features.betsPlaced && builder.options.betsPlaced.position === position
// No title (#946): the title and its space go; the tag, a header counter and the game stay.
const titleless = (builder) => builder.banner === BANNER_IMAGE && builder.title?.placement === TITLE_PLACEMENT_NONE
// A header with nothing that could show in it is not written, so it leaves no gap. With a badge it is: under a
// whole-card picture it holds the content off the badge (TITLE_NONE_CARD_BADGE_CSS).
const headless = (builder) => titleless(builder) && !builder.features.betType
  && !counterAt(builder, 'header') && !counterAt(builder, 'badge') && !layoutShowsEvent(builder.layout)
// A hero header's counter floats at its top right, so a title with no tag above it starts under the counter
// (TITLE_COUNTER_CSS), whatever its alignment and banner (#950), but the split banner (BANNER_SPLIT), whose header
// keeps the counter's side.
const titleUnderCounter = (builder) => layoutShowsEvent(builder.layout) && counterAt(builder, 'header') && builder.banner !== BANNER_SPLIT && !titleless(builder)

// A header picture goes behind the title over it, else in a band above the header (#946).
function bannerCss(builder) {
  if (builder.banner !== BANNER_IMAGE) return BANNER_CSS[builder.banner]
  if (fillsCard(builder)) return fillsFullCard(builder) ? IMAGE_CARD_FULL_CSS : IMAGE_CARD_CSS
  return builder.title.placement === TITLE_PLACEMENT_OVER ? IMAGE_BANNER_CSS : IMAGE_BAND_CSS
}

// The title's place and alignment (#946). Without a title a header that is left empty hides (behind the whole card
// the one kept for a badge holds the content off it, and without a header the tagline keeps its room under the
// band); a centered title over the picture also gives the header back the room kept clear of a badge and over the
// header picture evens the wash. Over and left-aligned writes nothing (a hero header's counter is titleUnderCounter's).
function titleCss(builder) {
  if (builder.banner !== BANNER_IMAGE) return ''
  if (builder.title.placement === TITLE_PLACEMENT_NONE) {
    return titleNoneCss(layoutShowsEvent(builder.layout)) + (fillsCard(builder) ? TITLE_NONE_CARD_BADGE_CSS : '') + (headless(builder) ? TITLE_NONE_HEADLESS_CSS : '')
  }
  if (builder.title.alignment !== TITLE_ALIGNMENT_CENTER) return ''
  const over = builder.title.placement === TITLE_PLACEMENT_OVER
  return TITLE_CENTER_CSS + (over ? TITLE_CENTER_BADGE_CSS : '') + (over && !fillsCard(builder) ? IMAGE_BANNER_CENTER_CSS : '')
}

// Team marks (#572): with logos or jerseys on, the icon cell carries an image
// per enabled feature and the sport glyph, and MARK_CSS shows one of them.
const hasTeamMarks = (features) => features.logos || features.jerseys

// A template that hides shirt numbers draws the plain jersey, so a number the
// pack carries for another template never shows on this one.
const jerseyToken = (index, options, forcePlain = false) => (forcePlain || JERSEY_NUMBERS_RETIRED || options?.jerseys?.numbers === 'hidden' ? `LEG_${index}_JERSEY_PLAIN` : `LEG_${index}_JERSEY`)

function legIconMarkup(index, features, options, forcePlainJerseys = false) {
  if (!hasTeamMarks(features)) return features.legIcons ? `<span class="leg-icon">{{LEG_${index}_GLYPH}}</span>` : ''
  const logo = features.logos ? `<img class="leg-logo" src="{{LEG_${index}_LOGO}}" alt="">` : ''
  const jersey = features.jerseys ? `<img class="leg-jersey" src="{{${jerseyToken(index, options, forcePlainJerseys)}}}" alt="">` : ''
  return `<span class="leg-icon" data-mark="{{LEG_${index}_MARK}}">${logo}${jersey}<span class="leg-glyph">{{LEG_${index}_GLYPH}}</span></span>`
}

// MARK_CSS and LAYOUT_MARK_CSS live in bannerFills.js: the widget's fill swap places the card fill's mark CSS after them.

function legMarkup(index, features, options, forcePlainJerseys = false) {
  const icon = legIconMarkup(index, features, options, forcePlainJerseys)
  const side = [
    features.matchup ? `<span class="leg-event">{{LEG_${index}_EVENT}}</span>` : '',
    features.legOdds ? `<span class="leg-odds">{{LEG_${index}_ODDS}}</span>` : '',
  ].join('')
  return `<div class="leg">${icon}<span class="leg-copy"><strong>{{LEG_${index}_PLAYER}}</strong><small>{{LEG_${index}_BET}}</small></span>${side ? `<span class="leg-side">${side}</span>` : ''}</div>`
}

// Icons for the bets-placed counter, inline so the card needs no asset (paths from Lucide, ISC).
const ICON_PATHS = {
  people: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  flame: '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
  chart: '<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>',
  trophy: '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>',
  star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  bolt: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>',
}
export const COUNTER_ICONS = Object.keys(ICON_PATHS)
export function counterIcon(id) {
  return ICON_PATHS[id] ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON_PATHS[id]}</svg>` : ''
}
const FONT_CSS = {
  system: '',
  oswald: `${BUILDER_FONT_FACES.oswald}.title{${OSWALD_TITLE}}`,
  lilita: `${BUILDER_FONT_FACES.lilita}.title{font-family:${BUILDER_FONT_FAMILIES.lilita};font-weight:400;letter-spacing:.01em}`,
}
const BORDER_WIDTH = { thin: '1px', medium: '2px', thick: '4px' }
/** Layouts whose look is a specific font; the title font choice is ignored for them. */
export function layoutHasOwnFont(layout) {
  return layout === 'editorial'
}

function builderHtml(builder, { forcePlainJerseys = false } = {}) {
  const { features, options } = builder
  const legs = [1, 2, 3, 4, 5, 6].map((index) => legMarkup(index, features, options, forcePlainJerseys)).join('')
  const position = features.betsPlaced ? options.betsPlaced.position : ''
  const counter = `${counterIcon(options.betsPlaced.icon)}<span>{{COPIES}} {{COPIES_LABEL}}</span>`
  const placed = (extra) => `<span class="placed${extra}">${counter}</span>`
  const tag = features.betType ? '<span class="tag">{{TYPE}}<span class="tag-count"> · {{LEG_COUNT}} {{LEG_COUNT_LABEL}}</span></span>' : ''
  const packTitle = titleless(builder) ? '' : '<span class="title">{{TITLE}}</span>'
  const head = headless(builder) ? '' : `<div class="head">${tag}${packTitle}${position === 'header' ? placed('') : ''}<span class="event">{{EVENT}}</span></div>`
  const summary = features.summary ? '<div class="summary">{{SUMMARY}}</div>' : ''
  return `<div class="card">${position === 'badge' ? placed(' placed-badge') : ''}${head}<div class="subtitle">{{SUBTITLE}}</div>${summary}<div class="legs">${legs}</div>${position === 'below' ? `<div class="placed">${counter}</div>` : ''}</div>`
}

// The real button, odds and card frame are drawn by the widget around the
// artwork; these settings point them at the template's own variables. Under
// a picture that fills the card the footer takes the scrim's color, so the
// card ends in the same tone it fades to, or nothing at all when the picture
// runs on under it (#1038).
function nativeBranding(builder) {
  const { layout, features, options } = builder
  const accentBorder = layout === 'accent-border' || features.cardBorder
  return {
    layout: {
      cta: { buttonHeight: '44px', paddingX: '14px', paddingY: '8px', borderRadius: '10px', actionsPaddingX: '14px', actionsPaddingY: '12px' },
      card: { borderRadius: '14px', borderWidth: features.cardBorder ? BORDER_WIDTH[options.cardBorder.width] : accentBorder ? '2px' : '1px' },
    },
    typography: {
      cta: { fontSize: '13px', fontWeight: '700', lineHeight: '1.2', letterSpacing: '0px', textTransform: 'none', fontStyle: 'normal' },
      odds: { fontSize: '15px', fontWeight: '800', lineHeight: '1.2', letterSpacing: '0px', textTransform: 'none', fontStyle: 'normal' },
    },
    effects: { ctaShadow: 'none' },
    components: {
      prepackActions: { background: fillsFullCard(builder) ? 'transparent' : fillsCard(builder) ? CARD_PICTURE_FOOTER : accentBorder ? 'var(--row)' : 'var(--bg)', borderColor: 'transparent' },
      prepackOdds: { background: 'transparent', color: 'var(--accent-text)', borderColor: 'transparent', borderWidth: '0px', borderRadius: '6px', paddingX: '0px', paddingY: '0px' },
      cta: {
        background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'transparent', borderWidth: '0px', borderBottomWidth: '0px',
        hoverBackground: 'var(--feed-component-cta-bg)', hoverBorderColor: 'var(--feed-component-cta-border-color)',
        activeBackground: 'var(--feed-component-cta-bg)', activeColor: 'var(--feed-component-cta-color)', activeBorderColor: 'var(--feed-component-cta-border-color)',
      },
    },
    colors: { surface: 'var(--bg)', heading: 'var(--text)', subheading: 'var(--text-dim)', borderColor: accentBorder ? 'var(--accent)' : 'var(--divider)' },
  }
}

/** The native-shell settings the builder lets an operator override, as branding paths. */
export const SHELL_FIELDS = [
  ['components.cta.background', 'Button background', 'color'],
  ['components.cta.color', 'Button text', 'color'],
  ['components.cta.borderColor', 'Button border', 'color'],
  ['components.cta.borderWidth', 'Button border width'],
  ['layout.cta.borderRadius', 'Button corner radius'],
  ['typography.cta.fontSize', 'Button font size'],
  ['components.prepackOdds.background', 'Odds background', 'color'],
  ['components.prepackOdds.color', 'Odds text', 'color'],
  ['components.prepackOdds.borderColor', 'Odds border', 'color'],
  ['typography.odds.fontSize', 'Odds font size'],
  ['components.prepackActions.background', 'Footer background', 'color'],
  ['components.prepackActions.borderColor', 'Footer border', 'color'],
  ['layout.card.borderRadius', 'Card corner radius'],
  ['layout.card.borderWidth', 'Card border width'],
  ['colors.borderColor', 'Card border', 'color'],
]
const SHELL_PATHS = new Set(SHELL_FIELDS.map(([path]) => path))

function withShell(branding, shell) {
  const result = structuredClone(branding)
  for (const [path, value] of Object.entries(shell)) {
    const keys = path.split('.')
    let group = result
    for (const key of keys.slice(0, -1)) group = group[key] ||= {}
    group[keys.at(-1)] = value
  }
  return result
}

// A sportsbranding catalog team id ("nfl:phi"); the server checks it against the catalog on save.
const TEAM_ID = /^[a-z0-9-]+:[a-z0-9-]+$/
// The added CSS's fields (#998): appended to the shared CSS and to each mode's variant CSS.
export const ADDED_CSS_KEYS = ['shared', ...BUILDER_MODES]
// No added CSS: every field empty.
export const EMPTY_ADDED_CSS = Object.freeze(Object.fromEntries(ADDED_CSS_KEYS.map((key) => [key, ''])))
// What the server refuses on save, in UTF-8 bytes: an added CSS field over MaxTemplateBuilderCSSBytes
// (template_builder.go), and a template whose HTML and CSS together pass the aggregate limit (rules.go).
const MAX_ADDED_CSS_BYTES = 64 * 1024
const MAX_TEMPLATE_CODE_BYTES = 200000
const utf8 = new TextEncoder()
const byteLength = (value) => utf8.encode(value).length

/** Fill in anything a set of choices leaves out or gets wrong, so every caller works with a complete, valid set. */
export function normalizeBuilderChoices(choices) {
  const layout = BUILDER_LAYOUTS.some((item) => item.id === choices?.layout) ? choices.layout : BUILDER_DEFAULTS.layout
  const banner = BUILDER_BANNERS.some((item) => item.id === choices?.banner) ? choices.banner : BUILDER_DEFAULTS.banner
  const features = {}
  for (const feature of BUILDER_FEATURES) {
    const value = choices?.features?.[feature.key]
    features[feature.key] = typeof value === 'boolean' ? value : BUILDER_DEFAULTS.features[feature.key]
  }
  const palette = [0, 1].map((index) => (isHexColor(choices?.palette?.[index]) ? choices.palette[index].trim().toLowerCase() : BUILDER_DEFAULTS.palette[index]))
  const overrides = {}
  for (const mode of BUILDER_MODES) {
    overrides[mode] = {}
    for (const [key] of VARIABLES) {
      const value = choices?.overrides?.[mode]?.[key]
      if (isHexColor(value)) overrides[mode][key] = value.trim().toLowerCase()
    }
  }
  const font = BUILDER_FONTS.some((item) => item.id === choices?.font) ? choices.font : BUILDER_DEFAULTS.font
  const options = {}
  for (const feature of BUILDER_FEATURES) {
    if (!feature.options) continue
    options[feature.key] = {}
    for (const group of feature.options) {
      const value = choices?.options?.[feature.key]?.[group.key]
      options[feature.key][group.key] = group.values.some((option) => option.id === value) ? value : BUILDER_DEFAULTS.options[feature.key][group.key]
    }
  }
  // Native button, odds and footer styling, as path -> value per mode, on top of what the layout sets.
  const shell = {}
  for (const mode of BUILDER_MODES) {
    shell[mode] = {}
    for (const [path, value] of Object.entries(choices?.shell?.[mode] || {})) {
      if (SHELL_PATHS.has(path) && typeof value === 'string' && value.trim() && value.length <= 64) shell[mode][path] = value.trim()
    }
  }
  // HTML and CSS edited by hand: they replace what the choices generate and lock the layout, banner, font and features.
  const code = choices?.code && typeof choices.code === 'object'
    ? Object.fromEntries(['html', 'css', 'lightCss', 'darkCss'].map((key) => [key, typeof choices.code[key] === 'string' ? choices.code[key] : '']))
    : null
  // CSS added by hand (#998), after the generated CSS: shared, light card, dark card. It locks nothing and is kept,
  // inactive, while `code` is set. A field is kept as typed unless it is blank; all blank saves no key at all.
  const added = Object.fromEntries(ADDED_CSS_KEYS.map((key) => {
    const value = choices?.css?.[key]
    return [key, typeof value === 'string' && value.trim() !== '' ? value : '']
  }))
  const normalized = { layout, banner, font, features, options, palette, overrides, shell }
  // The template's team (#602): what the builder previews and took the colors from. It changes nothing
  // in the compiled card, and a template without one saves no key at all.
  if (typeof choices?.team === 'string' && TEAM_ID.test(choices.team)) normalized.team = choices.team
  if (banner === BANNER_IMAGE) {
    normalized.image = normalizeBannerImage(choices?.image)
    // Where the title sits on the picture (#946): beside the image, and only with it, as the server keeps it.
    normalized.title = bannerTitleOf(choices?.title, normalized.image.fill)
  }
  if (ADDED_CSS_KEYS.some((key) => added[key])) normalized.css = added
  return code ? { ...normalized, code } : normalized
}

/**
 * Choices to open in the builder. A template saved before #998 with `code` whose HTML is still exactly what its
 * choices generate had only its CSS edited: `code` is dropped and its CSS fields become `css`, with the generated
 * CSS stripped from their start. A field that does not start with it is kept whole as the added CSS (it comes after
 * the generated CSS, so its rules win on equal specificity; rules it deleted come back). Such a field also carries the
 * old generator's fill CSS, so on an image-banner template a schedule's fill swap changes only the generated part and
 * the appended copy still wins; the operator can trim it in Added CSS. A template whose HTML was edited stays locked,
 * and so does one the server would refuse once converted (a field kept whole over the added CSS limit, or the
 * generated CSS plus the kept field over the aggregate limit): it saves as it did before. Nothing is stored until the
 * operator saves.
 */
export function unlockCssOnlyEdits(choices) {
  const normalized = normalizeBuilderChoices(choices)
  if (!normalized.code) return normalized
  const { code, css: _ignored, ...rest } = normalized
  if (code.html !== builderHtml(rest) && code.html !== builderHtml(rest, { forcePlainJerseys: true })) return normalized
  const generated = compileBuilderTemplate(rest)
  const strip = (value, prefix) => (prefix && value.startsWith(prefix) ? value.slice(prefix.length) : value)
  const unlocked = normalizeBuilderChoices({
    ...rest,
    css: {
      shared: strip(code.css, generated.cssCode),
      light: strip(code.lightCss, generated.variants.light.cssCode),
      dark: strip(code.darkCss, generated.variants.dark.cssCode),
    },
  })
  const compiled = compileBuilderTemplate(unlocked)
  const codeBytes = byteLength(compiled.htmlCode) + byteLength(compiled.cssCode)
    + BUILDER_MODES.reduce((total, mode) => total + byteLength(compiled.variants[mode].cssCode), 0)
  const fieldTooLong = ADDED_CSS_KEYS.some((key) => byteLength(unlocked.css?.[key] || '') > MAX_ADDED_CSS_BYTES)
  return fieldTooLong || codeBytes > MAX_TEMPLATE_CODE_BYTES ? normalized : unlocked
}

/**
 * The image banner's picture settings: an optional fixed picture (url), whether packs and schedules may paint
 * their own (ai_allowed, with the default scene in prompt), where every picture goes (fill: the header, or the
 * whole card; an unknown fill is the header), behind the whole card what the native footer shows (footer: tinted, or
 * transparent so the picture runs on under it, #1038) and the framing. `source` is derived for older readers: "ai" when AI
 * is allowed with no fixed picture, else "library". A stored shape without ai_allowed (made before #606) reads
 * from its source; anything unusable reads as AI allowed with no scene.
 */
export function normalizeBannerImage(image) {
  const legacy = typeof image?.ai_allowed !== 'boolean'
  const aiAllowed = legacy ? image?.source !== 'library' : image.ai_allowed
  // An old "ai" shape never had a fixed picture; whatever url it carries is left over.
  const url = (legacy && aiAllowed) || !isBannerImageUrl(image?.url) ? '' : image.url
  const prompt = aiAllowed && typeof image?.prompt === 'string' ? clampBannerPrompt(image.prompt) : ''
  const fill = bannerFillOf(image)
  const position = bannerPositionOf(image?.position) ? image.position : defaultBannerPosition(fill)
  const scale = bannerScaleOf(image?.scale)
  const normalized = { ai_allowed: aiAllowed, source: aiAllowed && !url ? 'ai' : 'library', fill, url, prompt, position, scale }
  // The footer under the picture belongs to the whole card: a header picture stores none, as the server keeps it.
  if (fill === BANNER_FILL_CARD) normalized.footer = bannerFooterOf(image)
  return normalized
}

/** A scene as the server keeps it: one line of at most MAX_BANNER_PROMPT_RUNES runes. */
function clampBannerPrompt(value) {
  return Array.from(value.replace(WHITESPACE_RUN_PATTERN, ' ').trim()).slice(0, MAX_BANNER_PROMPT_RUNES).join('')
}

/** Why an image banner's picture settings cannot be saved, or null. template_builder.go refuses the same with the same words. */
export function bannerImageProblem(image) {
  if (image?.url && !isBannerImageUrl(image.url)) return 'The banner picture must be an https URL'
  const normalized = normalizeBannerImage(image)
  if (!normalized.ai_allowed && !normalized.url) return 'Pick a fixed picture or allow AI pictures'
  if (normalized.ai_allowed && !normalized.url && !normalized.prompt) return 'Describe the default scene, or pick a fixed picture'
  return null
}

/**
 * How a schedule's packs get their picture (#606): "dynamic" (the worker paints each pack), "static" (the
 * schedule's or the template's picture), or "" when the template has no image banner. `banner` is the
 * schedule's criteria.banner ({ mode, prompt, url, fill, hidden }) or null. Without a choice it is "dynamic" when
 * the template allows AI and has no fixed picture, else "static". Dynamic falls back to static, as a run does, when
 * the template does not allow AI or neither side has a scene. A schedule that turned its picture off (hidden,
 * issue 683) is static whatever it chose: nothing is painted. template_builder.go RunBannerMode is the same rule
 * (EffectiveBannerMode there has no fallback).
 */
export function effectiveBannerMode(template, banner) {
  const builder = template?.builder
  if (builder?.banner !== BANNER_IMAGE) return ''
  if (banner?.hidden === true) return BANNER_MODE_STATIC
  const image = normalizeBannerImage(builder.image)
  const chosen = banner?.mode === BANNER_MODE_DYNAMIC || banner?.mode === BANNER_MODE_STATIC ? banner.mode : ''
  const mode = chosen || (image.ai_allowed && !image.url ? BANNER_MODE_DYNAMIC : BANNER_MODE_STATIC)
  if (mode !== BANNER_MODE_DYNAMIC) return BANNER_MODE_STATIC
  const scene = (chosen && typeof banner.prompt === 'string' ? banner.prompt.trim() : '') || image.prompt
  return image.ai_allowed && scene ? BANNER_MODE_DYNAMIC : BANNER_MODE_STATIC
}

/**
 * Where a schedule's pictures go (issue 683): its own fill (criteria.banner.fill) when it chose a valid one,
 * else the template's; "" when the template has no image banner. A template with hand-edited code keeps its
 * own fill, since its CSS is not the builder's. ScheduleBannerFill in template_builder.go is its twin: a run
 * stamps the schedule's fill on each pack (BANNER_FILL_VARIABLE) and packBannerFill reads it back.
 */
export function scheduleBannerFill(template, banner) {
  const builder = template?.builder
  if (builder?.banner !== BANNER_IMAGE) return ''
  const own = bannerFillOf(builder.image)
  return !builder.code && BANNER_FILLS.includes(banner?.fill) ? banner.fill : own
}

/** Where a pack's picture goes: the fill its schedule stamped in its variables, else the template's (PackBannerFill in template_builder.go). */
export function packBannerFill(template, vars) {
  return scheduleBannerFill(template, { fill: vars?.[BANNER_FILL_VARIABLE] })
}

/**
 * The framing variables a schedule's static picture choice stamps on each pack (ApplyScheduleBanner in
 * template_builder.go, #978), in `fill`: the focus and the zoom it set, none for one it did not, so the
 * template's framing applies. The sample card draws them (scheduleSampleBannerVars).
 */
export function scheduleBannerFramingVars(banner, fill) {
  const vars = {}
  if (bannerPositionOf(banner?.position)) vars[BANNER_POSITION_VARIABLE] = banner.position
  if (isBannerScale(banner?.scale)) vars[BANNER_SIZE_VARIABLE] = bannerImageSize(banner.scale, fill)
  return vars
}

/** The color variables of one mode: the placed palette with the operator's individual adjustments on top. */
export function builderVariables(choices, mode) {
  return variablesFor(normalizeBuilderChoices(choices), mode)
}

function variablesFor(normalized, mode) {
  const placed = { ...placePalette(normalized.palette, mode), ...normalized.overrides[mode] }
  const variables = VARIABLES.map(([key, label]) => ({ key, label, value: placed[key], type: 'color' }))
  if (normalized.banner === BANNER_IMAGE) {
    // The template's fixed picture; without one a pack's own (picked or painted) arrives in its variables.
    const { url } = normalized.image
    variables.push({ key: BANNER_IMAGE_VARIABLE, label: 'Banner image', value: url ? bannerImageValue(url) : 'none', type: 'text' })
    variables.push({ key: BANNER_POSITION_VARIABLE, label: 'Banner focus', value: normalized.image.position, type: 'text' })
    variables.push({ key: BANNER_SIZE_VARIABLE, label: 'Banner zoom', value: bannerImageSize(normalized.image.scale, normalized.image.fill), type: 'text' })
  }
  return variables
}

/** Compile builder choices into a card template. `base` carries id, name, description and isDefault. */
export function compileBuilderTemplate(choices, base = {}) {
  const builder = normalizeBuilderChoices(choices)
  // CSS added by hand (#998) goes after everything generated; hand-edited code already carries it.
  const added = builder.css || EMPTY_ADDED_CSS
  const variants = {}
  for (const mode of BUILDER_MODES) {
    variants[mode] = {
      cssVars: variablesFor(builder, mode),
      cssCode: builder.code ? builder.code[`${mode}Css`] : `html{color-scheme:${mode}}` + added[mode],
      branding: withShell(nativeBranding(builder), builder.shell[mode]),
    }
  }
  return {
    id: base.id,
    name: base.name || '',
    description: base.description || '',
    // Hand-edited code wins over what the layout, banner and features would generate (and over the added CSS).
    htmlCode: builder.code ? builder.code.html : builderHtml(builder),
    // Editorial is its font: the title font choice does not apply to it. The title's alignment comes before the
    // team marks, so the card fill's mark CSS ends the generated CSS. The added CSS comes after all of it. Keep this
    // order: withCardMarkCss (bannerFills.js) finds the mark block by its text and puts the card fill's mark CSS after it.
    cssCode: builder.code ? builder.code.css : BASE_CSS + LAYOUT_CSS[builder.layout] + bannerCss(builder) + (layoutHasOwnFont(builder.layout) ? '' : FONT_CSS[builder.font])
      + titleCss(builder) + (titleUnderCounter(builder) ? TITLE_COUNTER_CSS : '')
      + (hasTeamMarks(builder.features) ? MARK_CSS + (LAYOUT_MARK_CSS[builder.layout] || '') + (fillsCard(builder) ? IMAGE_CARD_MARK_CSS : '') : '')
      + added.shared,
    cssVars: variants.dark.cssVars,
    variants,
    nativeAction: builder.features.buttonOdds ? 'odds' : 'label',
    isDefault: Boolean(base.isDefault),
    isFallback: false,
    builder,
  }
}
